import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Paper,
  Skeleton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { roundToZeroDecimals } from 'utils';
import { SimpleSelect } from 'components/CustomSelectFields';
import { getLeaderboardOptions } from 'components/Analytics/constant';

const LeaderBoard = ({
  data = { topPerformers: [], bestPerformers: [] },
  loading = false,
  leaderboardCategory,
  setLeaderboardCategory,
  error = null,
  interviewVariantType = null,
  // Scores on a fractional scale (case 0-10) lose all resolution when rounded
  // to whole numbers, so callers on those scales ask for decimals.
  scoreDecimals = 0,
  scoreLabel = 'Scores',
}) => {

  const formatScore = (value) => (
    scoreDecimals > 0
      ? Number(value || 0).toFixed(scoreDecimals)
      : roundToZeroDecimals(value || 0)
  );

  const navigate = useNavigate();

  // Low Score only exists for the case variants, so a category carried over
  // from another tab has to fall back before it reaches the request.
  const options = useMemo(
    () => getLeaderboardOptions(interviewVariantType),
    [interviewVariantType]
  );

  useEffect(() => {
    if (!options.some(option => option._id === leaderboardCategory)) {
      setLeaderboardCategory('best_score');
    }
  }, [options, leaderboardCategory, setLeaderboardCategory]);

  // Every category except Most dedicated ranks on the score column.
  const isScoreRanked = leaderboardCategory !== 'most_dedicated';

  const handleLeaderBoard = (e) => {
    const selectedValue = e.target.value;
    const selectedLeaderboard = options.find(result => result._id === selectedValue);
    setLeaderboardCategory(selectedLeaderboard._id);
  };

  const handleAnalyticsClick = (studentId) => {
    navigate(`/students/${studentId}`);
  };

  const currentData = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }
    
    return isScoreRanked ? (data.bestPerformers || []) : (data.topPerformers || []);
  }, [isScoreRanked, data, interviewVariantType]);

  const HeaderControls = () => {
    return (
      <Box display="flex" flexDirection="column" gap="12px">
        <Typography variant="h4-medium">Leaderboard</Typography>
          <SimpleSelect
            label="Filter performers"
            options={options}
            value={leaderboardCategory}
            onSelect={handleLeaderBoard}
            sx={{ width: '100%' }}
          />
      </Box>
    );
  };

  const TableHeader = () => (
    <Table>
      <TableHead sx={{ backgroundColor: '#F5F7FA' }}>
        <TableRow>
          <TableCell>
            <Typography variant='body01-bold'>Student name</Typography>
          </TableCell>
          <TableCell>
            <Typography variant='body01-bold'>
              {isScoreRanked ? scoreLabel : 'Total Attempts'}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant='body01-bold'>Analytics</Typography>
          </TableCell>
        </TableRow>
      </TableHead>
    </Table>
  );

  const ContainerPaper = ({ children }) => (
    <Paper
      elevation={0}
      sx={{ border: '1px solid #CCD4DE', borderRadius: '8px', overflow: 'hidden' }}
    >
      {children}
    </Paper>
  );

  const LeaderboardSkeleton = () => (
    <Box display="flex" flexDirection="column" gap="16px" width="100%">
      <HeaderControls />
      <ContainerPaper>
        <TableHeader />
        <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
          <Table>
            <TableBody>
              {Array.from({ length: 4 }).map((_, index) => (
                <TableRow key={index} sx={{ borderBottom: '1px solid #CCD4DE' }}>
                  <TableCell>
                    <Skeleton variant="text" width={150} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={60} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="circular" width={24} height={24} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </ContainerPaper>
    </Box>
  );

  if (loading) {
    return <LeaderboardSkeleton />;
  }

  const LeaderboardEmptyState = () => (
    <Box display="flex" flexDirection="column" gap="16px" width="100%">
      <HeaderControls />
      <ContainerPaper>
        <TableHeader />
        <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
          <Table>
            <TableBody>
              {Array.from({ length: 4 }).map((_, index) => (
                <TableRow key={index} sx={{ borderBottom: '1px solid #CCD4DE' }}>
                  <TableCell>
                    <Typography variant='body01-medium' color="neutral.clr-600">
                      N/A
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body01-medium' color="neutral.clr-600">
                      N/A
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" disabled>
                      <TrendingUpIcon sx={{ color: '#CCD4DE' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </ContainerPaper>
    </Box>
  );

  if (error) {
    return <LeaderboardEmptyState />;
  }

  return (
    <Box display="flex" flexDirection="column" gap="16px" width="100%">
      <HeaderControls />
      <ContainerPaper>
        <TableHeader />
        <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
          <Table>
            <TableBody>
              {currentData.length > 0 ? (
                currentData.map((row, index) => (
                  <TableRow key={row._id || index} sx={{ borderBottom: '1px solid #CCD4DE' }}>
                    <TableCell>
                      <Typography variant='body01-medium'>
                        {row.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body01-medium'>
                        {isScoreRanked
                          ? formatScore(row.average)
                          : roundToZeroDecimals(row.totalAttempts || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleAnalyticsClick(row._id)}>
                        <TrendingUpIcon sx={{ color: '#003E8C' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant='body01-medium' textAlign="center" py={2}>
                      No leaderboard data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </ContainerPaper>
    </Box>
  );
};

export default LeaderBoard;
